import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Card, Descriptions, Tag, Row, Col, Switch, message, Button, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { ActivityLogList } from './ActivityLogList';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

interface Staff {
  id: number;
  username: string;
  fullName: string | null;
  phone: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  permissions: Record<string, string[]>;
}

const permissionLabels: Record<string, string> = {
  read: t('staff.actions.read'),
  create: t('staff.actions.create'),
  update: t('staff.actions.update'),
  delete: t('staff.actions.delete'),
  block: t('staff.actions.block'),
  verify: t('staff.actions.verify'),
  review: t('staff.actions.review'),
  reply: t('staff.actions.reply'),
  resolve: t('staff.actions.resolve'),
  cancel: t('staff.actions.cancel'),
};

const groupLabels: Record<string, string> = {
  users: t('staff.permissionGroups.users'),
  drivers: t('staff.permissionGroups.drivers'),
  documents: t('staff.permissionGroups.documents'),
  trips: t('staff.permissionGroups.trips'),
  payments: t('staff.permissionGroups.payments'),
  support: t('staff.permissionGroups.support'),
  faq: t('staff.permissionGroups.faq'),
  promo: t('staff.permissionGroups.promo'),
  settings: t('staff.permissionGroups.settings'),
  staff: t('staff.permissionGroups.staff'),
};

export const StaffShow = () => {
  const { edit } = useNavigation();
  const { can } = usePermission();
  const { queryResult } = useShow<Staff>();
  const { data, isLoading, refetch } = queryResult;
  const staff = data?.data;

  const handleToggleActive = async (checked: boolean) => {
    try {
      await apiClient.put(`/admin/staff/${staff?.id}`, { isActive: checked });
      message.success(t('common.success'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  return (
    <Show
      isLoading={isLoading}
      title={`Xodim: ${staff?.fullName || staff?.username}`}
      headerButtons={
        can('staff', 'update') ? (
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => edit('admin/staff', staff?.id || 0)}
            >
              {t('common.edit')}
            </Button>
          </Space>
        ) : null
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={t('staff.basicInfo')}>
            <Descriptions column={1}>
              <Descriptions.Item label="ID">{staff?.id}</Descriptions.Item>
              <Descriptions.Item label={t('staff.login')}>{staff?.username}</Descriptions.Item>
              <Descriptions.Item label={t('staff.fullName')}>
                {staff?.fullName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('staff.phone')}>
                {staff?.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>
                <Switch
                  checked={staff?.isActive}
                  onChange={handleToggleActive}
                  disabled={!can('staff', 'update')}
                  checkedChildren={t('common.active')}
                  unCheckedChildren={t('common.inactive')}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('common.createdAt')}>
                {staff?.createdAt
                  ? new Date(staff.createdAt).toLocaleString('uz-UZ')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('staff.lastLogin')}>
                {staff?.lastLoginAt
                  ? new Date(staff.lastLoginAt).toLocaleString('uz-UZ')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('staff.permissions')}>
            {staff?.permissions &&
              Object.entries(staff.permissions).map(([group, perms]) => (
                <div key={group} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                    {groupLabels[group] || group}
                  </div>
                  <div>
                    {(perms as string[]).map((perm) => (
                      <Tag key={perm} color="blue" style={{ marginBottom: 4 }}>
                        {permissionLabels[perm] || perm}
                      </Tag>
                    ))}
                    {(perms as string[]).length === 0 && (
                      <span style={{ color: '#7F8C8D' }}>{t('staff.noPermissions')}</span>
                    )}
                  </div>
                </div>
              ))}
          </Card>
        </Col>

        {/* Activity Log */}
        <Col xs={24}>
          {staff?.id && (
            <ActivityLogList
              staffId={staff.id}
              embedded
              title={t('staff.activityLog')}
            />
          )}
        </Col>
      </Row>
    </Show>
  );
};
